import { parseAppLinksEnabled, validateOrigin } from './origins.mjs';

/**
 * Build-time configuration for the prerendered site.
 *
 * The pages are static HTML, so every value that ends up in them — canonical
 * URLs, the sign-up and sign-in links — has to be known when the site is
 * built. Each environment (local, preview, production) is therefore its own
 * build, configured with three environment variables:
 *
 *   APP_ENV     local | preview | production (default: local). Only a
 *               production build lets crawlers in (robots.txt) and sends HSTS.
 *   SITE_URL    The origin this build is served from, no trailing slash
 *               (default: http://localhost:8787 for local builds). Used for
 *               canonicals, hreflang, the sitemap and Open Graph URLs.
 *   APP_ORIGIN  The Landmate app's origin (https://go.landmate.app in
 *               production, https://go-preview.landmate.app in preview; see
 *               wrangler.jsonc). Optional. Unset means the app is not open to the public yet:
 *               every sign-up and sign-in link points at the site's own
 *               /get-started page, which says so, instead of at a domain that
 *               does not answer.
 *   APP_LINKS_ENABLED  "true" or "false": whether "Get started" and "Sign in"
 *               may point at APP_ORIGIN. Empty means the environment's default:
 *               false in production (sign-up is not open to the public yet),
 *               true in preview and local. With it off, or without an
 *               APP_ORIGIN, the links go to /get-started, which says so.
 *
 * The deployed values live in wrangler.jsonc (one block per environment);
 * scripts/deploy.mjs passes them to the build.
 */

export const appEnvironments = ['local', 'preview', 'production'] as const;
export type AppEnvironment = (typeof appEnvironments)[number];

export interface BuildConfig {
  appEnv: AppEnvironment;
  siteUrl: string;
  /** The app's origin, or null when none is configured. */
  appOrigin: string | null;
  /** Whether the sign-up / sign-in links may point at appOrigin (see APP_LINKS_ENABLED). */
  appLinksEnabled: boolean;
}

export interface RawBuildConfig {
  APP_ENV?: string | undefined;
  SITE_URL?: string | undefined;
  APP_ORIGIN?: string | undefined;
  APP_LINKS_ENABLED?: string | undefined;
}

export const LOCAL_SITE_URL = 'http://localhost:8787';

export function resolveBuildConfig(raw: RawBuildConfig): BuildConfig {
  const envValue = (raw.APP_ENV ?? '').trim() || 'local';
  if (!(appEnvironments as readonly string[]).includes(envValue)) {
    throw new Error(`APP_ENV must be one of ${appEnvironments.join(', ')} (got "${envValue}")`);
  }
  const appEnv = envValue as AppEnvironment;
  const deployed = appEnv !== 'local';

  const siteValue = (raw.SITE_URL ?? '').trim();
  if (!siteValue && deployed) {
    throw new Error(`SITE_URL is required for an APP_ENV=${appEnv} build`);
  }
  // Both origins are validated and normalized by the one shared module
  // (src/shared/origins.mjs): public, non-reserved https hosts for a deployed
  // build; https, or http on localhost, for a local one.
  const context = deployed ? 'public' : 'local';
  const siteUrl = validateOrigin('SITE_URL', siteValue || LOCAL_SITE_URL, { context });

  const appValue = (raw.APP_ORIGIN ?? '').trim();
  const appOrigin = appValue ? validateOrigin('APP_ORIGIN', appValue, { context }) : null;

  // Shared with the deploy preflight (src/shared/origins.mjs).
  const appLinksEnabled = parseAppLinksEnabled(raw.APP_LINKS_ENABLED, appEnv);

  return { appEnv, siteUrl, appOrigin, appLinksEnabled };
}
